import { cn } from '../cn';

import type { TextareaHTMLAttributes } from 'react';

export interface TextareaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange'
> {
  value: string;
  /** Receives the new string, not the event (as `Input` does). */
  onChange: (value: string) => void;
  /** The monospace variant for code-like text: 13 px, line height 1.75. */
  mono?: boolean | undefined;
  /** Class for the bordered box, for example a margin. */
  className?: string | undefined;
}

/**
 * Multi-line text box: a 1 px default border, --radius-lg, 12 × 16 px padding, the info
 * border and the focus ring while focused, and a vertical resize handle. The text is 14 px
 * with line height 1.6; `mono` makes it 13 px monospace with line height 1.75.
 */
export function Textarea({ value, onChange, mono = false, className, ...rest }: TextareaProps) {
  return (
    <div
      className={cn(
        'rounded-lg border border-border-default px-4 py-3 transition-[border-color,box-shadow] duration-150',
        'focus-within:border-surface-static-info-accented focus-within:shadow-focus-ring',
        className,
      )}
    >
      <textarea
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className={cn(
          'block min-h-20 w-full resize-y border-none bg-transparent text-content-default outline-none placeholder:text-content-placeholder',
          mono ? 'font-mono text-[13px] leading-[1.75]' : 'text-sm leading-[1.6]',
        )}
        {...rest}
      />
    </div>
  );
}
